import { Entity } from '@api/api/entity-schema';
import { RangeHorizon, ResampleIntervals } from '@chart';
import { Card } from '@design/components/card/Card';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { DailyUpdateCard } from '@terminal/components/daily-update-card/DailyUpdateCard';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { ErrorMessageWrapper } from '@terminal/components/error-message-wrapper/ErrorMessageWrapper';
import { MonthlySentimentCards } from '@terminal/components/monthly-sentiment/MonthlySentimentCards';
import { useChartNavigate } from '@terminal/hooks/use-chart-navigate/useChartNavigate';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { SimpleChart } from '@terminal/modules/simple-chart/SimpleChart';
import { useFilteredInsights } from '@terminal/react-query-hooks/insights/useFilteredInsights';
import { queryKeys } from '@terminal/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { ChartSearchParamsAssetValue } from '@terminal/utils/chart-search-utils/chart-search-utils';
import { SentimentBreakdownCard } from '@terminal/views/theme-overview/tab-content/pulse-overview/components/sentiment-breakdown/SentimentBreakdownCard';
import { MonthlySentimentDrawer } from '@terminal/views/theme-overview/tab-content/pulse-overview/drawer/MonthlySentimentDrawer';
import { usePulseSentimentBreakdown } from '@terminal/views/theme-overview/tab-content/pulse-overview/hooks/usePulseSentimentBreakdown';
import {
  usePulseDailyUpdate,
  usePulseMonthlySummaries,
} from '@terminal/views/theme-overview/tab-content/pulse-overview/hooks/usePulseSummaries';
import { PulseSentimentDirection } from '@terminal/views/theme-overview/tab-content/pulse-overview/pulse-types';
import { MENTIONS_WINDOW_DAYS } from '@terminal/views/theme-overview/tab-content/pulse-overview/services/theme-mentions-service';
import { ChartState } from '@terminal/views/turbo-chart/hooks/use-chart-state/useChartState';
import { ChartSettings } from '@terminal/views/turbo-chart/utils/chart-settings-utils/chart-settings-utils';
import { endOfDay, startOfDay, subDays } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { EarningsSection } from './components/earnings-section/EarningsSection';
import styles from './Overview.module.scss';

export interface OverviewProps {
  entity: Entity;
}

export const Overview = ({ entity }: OverviewProps) => {
  const { t } = useTranslation(['widget']);
  const { setQueryParams } = useQueryParams();
  const chartNavigate = useChartNavigate();
  const dailyUpdate = usePulseDailyUpdate({
    id: entity.tag,
    subjectType: 'entity',
  });
  const monthlySummaries = usePulseMonthlySummaries({
    id: entity.tag,
    subjectType: 'entity',
  });
  const { data: insights, isLoading: isInsightsLoading } = useFilteredInsights(
    {
      entity: [entity.tag],
      from_date: startOfDay(subDays(new Date(), MENTIONS_WINDOW_DAYS)),
      to_date: endOfDay(new Date()),
    },
    100
  );
  const sentimentBreakdown = usePulseSentimentBreakdown({
    id: entity.tag,
    subjectType: 'entity',
    scenarioInsights: insights?.data?.result ?? undefined,
  });

  const navigateToChart = (chartState: ChartState) => {
    const entityValue: ChartSearchParamsAssetValue = [
      entity.tag,
      entity.default_snake,
      0,
    ];

    chartNavigate([entityValue], {
      resample: chartState.resample,
      horizon: chartState.horizon,
      seriesType: chartState.chartPanes[0]?.seriesType,
    });
  };

  const openSentimentDrawer = (direction: PulseSentimentDirection) => {
    setQueryParams(
      { [queryKeys.monthlySentiment]: direction },
      { replace: true }
    );
  };

  const chartSettings: ChartSettings = {
    assets: [
      {
        tag: entity.tag,
        snake: entity.default_snake,
        paneIndex: 0,
      },
    ],
    resample: ResampleIntervals.OneMinute,
    horizon: RangeHorizon.OneDay,
    domain: null,
    hidden: undefined,
    paneSettings: {},
  };

  const isSummariesLoading = monthlySummaries.isLoading;
  const isSummariesEmpty =
    !monthlySummaries.data?.tailwind?.narrative &&
    !monthlySummaries.data?.headwind?.narrative;

  return (
    <div className={styles.overviewRoot}>
      <MonthlySentimentDrawer
        entityTag={entity.tag}
        subjectType="entity"
        source={POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW}
      />
      <SimpleChart
        entity={entity}
        key={entity.tag}
        initialChartSettings={chartSettings}
        isLegacyVariant={false}
        onClickFullscreen={navigateToChart}
        maxHorizon={RangeHorizon.TenYears}
        eventsConfig={{
          TMI: true,
        }}
        source={POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW}
      />
      <div className={styles.grid}>
        <div className={styles.gridSection}>
          <h3 className={styles.cardTitle}>
            {t('widget:assetOverview.sentiment.dailyUpdate')}
          </h3>
          <Card className={styles.card}>
            {dailyUpdate.isLoading ? (
              <SkeletonLoader
                areas={[
                  {
                    styles: {
                      height: '100px',
                      width: '100%',
                      borderRadius: '8px',
                    },
                  },
                ]}
              />
            ) : dailyUpdate.data?.content ? (
              <DailyUpdateCard
                update={dailyUpdate.data.content}
                source={POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW}
              />
            ) : (
              <ErrorMessageWrapper
                variant="empty"
                showBorder={false}
                className={styles.emptyErrorWrapper}
              />
            )}
          </Card>
        </div>
        <div className={styles.gridSection}>
          <ErrorBoundaryWrapper resetKeys={[entity.tag]}>
            <EarningsSection entity={entity} />
          </ErrorBoundaryWrapper>
        </div>
        {(!isSummariesEmpty || isSummariesLoading) && (
          <>
            <div className={styles.gridSection}>
              <h3 className={styles.cardTitle}>
                {t('widget:assetOverview.sentiment.monthlySentimentSummary')}
              </h3>
              {monthlySummaries.isLoading ? (
                <SkeletonLoader
                  className={styles.card}
                  areas={[
                    {
                      styles: {
                        height: '100%',
                        width: '100%',
                        borderRadius: '8px',
                      },
                    },
                  ]}
                />
              ) : (
                <MonthlySentimentCards
                  summaries={monthlySummaries.data}
                  source={POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW}
                  getCardTitle={direction =>
                    t(
                      `widget:assetOverview.sentiment.directionSentiment_${direction}`
                    )
                  }
                  onOpenDrawer={openSentimentDrawer}
                />
              )}
            </div>
            <div className={styles.gridSection}>
              <h3 className={styles.cardTitle}>
                {t('widget:assetOverview.sentiment.sentimentBreakdown')}
              </h3>
              <Card className={styles.card}>
                {sentimentBreakdown.isLoading || isInsightsLoading ? (
                  <SkeletonLoader
                    className={styles.card}
                    areas={[
                      {
                        styles: {
                          height: '100%',
                          width: '100%',
                          borderRadius: '8px',
                        },
                      },
                    ]}
                  />
                ) : sentimentBreakdown.data &&
                  Object.values(sentimentBreakdown.data.totals).some(
                    count => count > 0
                  ) ? (
                  <SentimentBreakdownCard
                    breakdown={sentimentBreakdown.data}
                    onOpenDrawer={openSentimentDrawer}
                  />
                ) : (
                  <ErrorMessageWrapper
                    variant="empty"
                    displayIcon
                    showBorder={false}
                  />
                )}
              </Card>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
